import PropTypes from 'prop-types';
import { useMemo } from 'react';

import { getSceneConfig } from '../config/storyScenes';

/**
 * Animated backdrop for scrollytelling chapters.
 * Renders layered shapes that crossfade between scenes based on active chapter.
 * Supports prefers-reduced-motion for accessibility.
 */
export default function ElectionStoryBackdrop({ activeChapterId }) {
  const scene = useMemo(() => getSceneConfig(activeChapterId), [activeChapterId]);

  const prefersReducedMotion =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
    <div className="fixed inset-0 -z-10 overflow-hidden">
      {/* Gradient background with scene transition */}
      <div
        className={`
          absolute inset-0
          bg-linear-to-br ${scene.color}
          ${prefersReducedMotion ? '' : 'transition-all duration-500'}
        `}
      />

      {/* Animated elements */}
      <div className="absolute inset-0 overflow-hidden">
        {scene.elements?.map((element) => {
          if (element.type === 'circle') {
            return (
              <div
                key={`circle-${element.position}-${element.size}`}
                className={`
                  absolute rounded-full
                  ${element.size}
                  ${element.position}
                  ${element.color}
                  ${element.opacity}
                  ${element.blur}
                  ${prefersReducedMotion ? '' : 'motion-safe:transition-all motion-safe:duration-700'}
                `}
              />
            );
          }

          if (element.type === 'rect') {
            return (
              <div
                key={`rect-${element.position}-${element.size}`}
                className={`
                  absolute
                  ${element.size}
                  ${element.position}
                  ${element.color}
                  ${element.opacity}
                  ${element.blur}
                  ${prefersReducedMotion ? '' : 'motion-safe:transition-all motion-safe:duration-700'}
                `}
              />
            );
          }

          if (element.type === 'checkmark') {
            return (
              <div
                key={`checkmark-${element.position}-${element.scale}`}
                className={`
                  absolute
                  ${element.position}
                  ${element.scale}
                  ${element.opacity}
                  ${prefersReducedMotion ? '' : 'motion-safe:transition-all motion-safe:duration-700'}
                `}
              >
                <svg
                  className="w-24 h-24 text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 12l2 2 4-4"
                  />
                </svg>
              </div>
            );
          }

          if (element.type === 'line') {
            return (
              <div
                key={`line-${element.position}`}
                className={`
                  absolute h-px bg-linear-to-r from-transparent via-current to-transparent
                  ${element.position}
                  ${element.opacity}
                  ${prefersReducedMotion ? '' : 'motion-safe:transition-opacity motion-safe:duration-700'}
                `}
              />
            );
          }

          return null;
        })}
      </div>

      {/* Subtle overlay to protect text readability */}
      <div className="absolute inset-0 bg-white/20" />
    </div>
  );
}

ElectionStoryBackdrop.propTypes = {
  activeChapterId: PropTypes.string,
};

ElectionStoryBackdrop.defaultProps = {
  activeChapterId: 'what-to-expect',
};
